import React, { useEffect } from 'react';
import { toast } from 'react-toastify';

const TimeSlotManager = ({ 
  timeSlots = [], 
  onTimeSlotsChange, 
  numberOfTrips, 
  onNumberOfTripsChange, 
  disabled = false,
  showArrival = false,
  showDeparture = true,
  labelPrefix = "Trip",
  onCopyToDestination = null
}) => {
  const [newTimeSlot, setNewTimeSlot] = React.useState({
    sequence: timeSlots.length + 1,
    departureTime: '',
    arrivalTime: '',
    fareMultiplier: 1.0,
    isActive: true
  });

  useEffect(() => {
    if (timeSlots.length !== numberOfTrips) {
      const newSlots = Array.from({ length: numberOfTrips }, (_, i) => ({
        sequence: i + 1,
        departureTime: timeSlots[i]?.departureTime || '',
        arrivalTime: timeSlots[i]?.arrivalTime || '',
        fareMultiplier: timeSlots[i]?.fareMultiplier || 1.0,
        isActive: timeSlots[i]?.isActive !== undefined ? timeSlots[i].isActive : true
      }));
      onTimeSlotsChange(newSlots);
    }
  }, [numberOfTrips]);

  const handleNumberOfTripsChange = (value) => {
    const num = parseInt(value) || 1;
    if (num > 10) {
      toast.warning('Maximum 10 trips per day allowed');
      return;
    }
    if (num < 1) {
      toast.warning('Minimum 1 trip per day required');
      return;
    }
    onNumberOfTripsChange(num);
  };

  const updateTimeSlot = (index, field, value) => {
    const updatedSlots = [...timeSlots];
    if (!updatedSlots[index]) {
      updatedSlots[index] = {
        sequence: index + 1,
        departureTime: '',
        arrivalTime: '',
        fareMultiplier: 1.0,
        isActive: true
      };
    }
    updatedSlots[index] = { ...updatedSlots[index], [field]: value };
    onTimeSlotsChange(updatedSlots);
  };

  const addTimeSlot = () => {
    if (timeSlots.length >= 10) {
      toast.warning('Maximum 10 trips per day allowed');
      return;
    }
    
    if (showDeparture && !newTimeSlot.departureTime) {
      toast.error('Please fill departure time');
      return;
    }
    if (showArrival && !newTimeSlot.arrivalTime) {
      toast.error('Please fill arrival time');
      return;
    }
    
    const updatedSlots = [...timeSlots, { 
      ...newTimeSlot, 
      sequence: timeSlots.length + 1 
    }];
    onTimeSlotsChange(updatedSlots);
    onNumberOfTripsChange(updatedSlots.length);
    
    setNewTimeSlot({
      sequence: updatedSlots.length + 1,
      departureTime: '',
      arrivalTime: '',
      fareMultiplier: 1.0,
      isActive: true
    });
    toast.success('Time slot added successfully');
  };

  const removeTimeSlot = (index) => {
    if (timeSlots.length <= 1) {
      toast.warning('At least one time slot is required');
      return;
    }
    const updatedSlots = timeSlots.filter((_, i) => i !== index);
    updatedSlots.forEach((slot, idx) => {
      slot.sequence = idx + 1;
    });
    onTimeSlotsChange(updatedSlots);
    onNumberOfTripsChange(updatedSlots.length);
    toast.success('Time slot removed');
  };

  const copyToDestination = () => {
    if (onCopyToDestination) {
      onCopyToDestination(timeSlots);
      toast.success('Trip schedule copied to destination');
    }
  };

  return (
    <div className="time-slot-manager" style={{ marginBottom: '20px' }}>
      <div className="form-group">
        <label>Number of Trips per Day</label>
        <input
          type="number"
          min="1"
          max="10"
          value={numberOfTrips}
          onChange={(e) => handleNumberOfTripsChange(e.target.value)}
          className="form-input"
          disabled={disabled}
          style={{ width: '100px' }}
        />
        <small className="helper-text">How many times this bus travels this route per day</small>
      </div>


      {onCopyToDestination && timeSlots.length > 0 && (
        <button
          type="button"
          onClick={copyToDestination}
          style={{
            marginBottom: '12px',
            padding: '4px 12px',
            fontSize: '11px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          📋 Copy schedule to destination
        </button>
      )}

      <div className="time-slots-list" style={{ marginTop: '15px' }}>
        <h5 style={{ marginBottom: '10px', fontSize: '14px', fontWeight: '500' }}>
          Trip Timings ({numberOfTrips} trip{numberOfTrips !== 1 ? 's' : ''} per day)
        </h5>


        {timeSlots.map((slot, index) => (
          <div key={index} className="time-slot-item" style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            marginBottom: '10px',
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ fontWeight: 'bold', minWidth: '60px' }}>
              {labelPrefix} #{slot.sequence || index + 1}
            </div>
            {showDeparture && (
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label style={{ fontSize: '12px', marginBottom: '2px' }}>Departure Time *</label>
                <input
                  type="time"
                  value={slot.departureTime || ''}
                  onChange={(e) => updateTimeSlot(index, 'departureTime', e.target.value)}
                  className="form-input"
                  style={{ fontSize: '13px', padding: '6px 8px' }}
                  required
                  disabled={disabled}
                />
              </div>
            )}
            {showArrival && (
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label style={{ fontSize: '12px', marginBottom: '2px' }}>Arrival Time *</label>
                <input
                  type="time"
                  value={slot.arrivalTime || ''}
                  onChange={(e) => updateTimeSlot(index, 'arrivalTime', e.target.value)}
                  className="form-input"
                  style={{ fontSize: '13px', padding: '6px 8px' }}
                  required
                  disabled={disabled}
                />
              </div>
            )}




            {timeSlots.length > 1 && !disabled && (
              <button
                type="button"
                onClick={() => removeTimeSlot(index)}
                className="btn btn-danger btn-sm"
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginTop: '18px'
                }}
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>


      {!disabled && timeSlots.length < 10 && (
        <div className="add-time-slot" style={{
          marginTop: '15px',
          padding: '15px',
          backgroundColor: '#f1f3f5',
          borderRadius: '8px'
        }}>
          <h6 style={{ marginBottom: '10px', fontSize: '13px', fontWeight: '500' }}>Add Additional Trip</h6>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {showDeparture && (
              <div className="form-group" style={{ flex: 1, minWidth: '150px', marginBottom: 0 }}>
                <label style={{ fontSize: '12px' }}>Departure Time</label>
                <input
                  type="time"
                  value={newTimeSlot.departureTime}
                  onChange={(e) => setNewTimeSlot(prev => ({ ...prev, departureTime: e.target.value }))}
                  className="form-input"
                  style={{ fontSize: '13px', padding: '6px 8px' }}
                />
              </div>
            )}
            {showArrival && (
              <div className="form-group" style={{ flex: 1, minWidth: '150px', marginBottom: 0 }}>
                <label style={{ fontSize: '12px' }}>Arrival Time</label>
                <input
                  type="time"
                  value={newTimeSlot.arrivalTime}
                  onChange={(e) => setNewTimeSlot(prev => ({ ...prev, arrivalTime: e.target.value }))}
                  className="form-input"
                  style={{ fontSize: '13px', padding: '6px 8px' }}
                />
              </div>
            )}

            <button
              type="button"
              onClick={addTimeSlot}
              className="btn btn-secondary"
              style={{ padding: '6px 16px', fontSize: '12px', marginTop: '18px' }}
            >
              + Add Trip
            </button>
          </div>
        </div>
      )}


      {!disabled && timeSlots.length >= 10 && (
        <small style={{ display: 'block', marginTop: '10px', color: '#dc3545' }}>
          Maximum 10 trips per day reached
        </small>
      )}
    </div>
  );
};

export default TimeSlotManager;